import { FC, useCallback, useEffect, useMemo, useState } from "react"
import dayjs, { Dayjs } from "utils/dayjs"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import Modal from "components/Modal/Modal"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import Lmid from "components/Lmid/Lmid"
import FormField from "components/FormField/FormField"
import InputDateTime from "components/InputDateTime/InputDateTime"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Spinner from "components/Spinner/Spinner"
import Select, { ISelectOption } from "components/Select/Select"
import Routes from "@/routes"
import { Guid } from "@/types"
import AssetService from "services/AssetService"
import useBookingPeriod from "hooks/useBookingPeriod"
import useMonthlyClosings from "hooks/useMonthlyClosings"
import toastMessages from "stores/toastMessages"
import confirmModals from "stores/confirmModals"

interface IReactivateAssetModalProps {
    assetId: Guid
    lmid: string
    articleDescription?: string
    relatedRequestNumber?: string
    relatedRequestId?: Guid
    onClose: () => void
    onSuccess?: () => void
}

const ReactivateAssetModal: FC<IReactivateAssetModalProps> = ({
    assetId,
    lmid,
    articleDescription,
    relatedRequestNumber,
    relatedRequestId,
    onClose,
    onSuccess,
}) => {
    const { t, i18n } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [availabilityDate, setAvailabilityDate] = useState<Dayjs | null>(dayjs().startOf("day"))
    const [minAvailabilityDate, setMinAvailabilityDate] = useState<Dayjs | null>(null)
    const [isLoadingAvailabilityBounds, setIsLoadingAvailabilityBounds] = useState(true)
    const currentBookingPeriod = useBookingPeriod()
    const { monthlyClosings, isLoadingMonthlyClosings, refetchMonthlyClosings } = useMonthlyClosings()
    const [selectedBookingPeriodKey, setSelectedBookingPeriodKey] = useState<string | null>(null)

    useEffect(() => {
        let isMounted = true
        setIsLoadingAvailabilityBounds(true)

        AssetService.getReactivationAvailabilityDateBounds(assetId)
            .then(result => {
                if (!isMounted) return
                setMinAvailabilityDate(result.reactivationLowerBoundDate)
                setAvailabilityDate(current => {
                    if (!current) return result.reactivationLowerBoundDate || dayjs().startOf("day")
                    if (result.reactivationLowerBoundDate && current.isBefore(result.reactivationLowerBoundDate)) {
                        return result.reactivationLowerBoundDate.clone()
                    }
                    return current
                })
            })
            .finally(() => {
                if (!isMounted) return
                setIsLoadingAvailabilityBounds(false)
            })

        return () => {
            isMounted = false
        }
    }, [assetId])

    const isAvailabilityDateInvalid = useMemo(() => {
        if (!availabilityDate || !minAvailabilityDate) return false
        return availabilityDate.isBefore(minAvailabilityDate)
    }, [availabilityDate, minAvailabilityDate])

    const bookingPeriodFormatter = useMemo(() => {
        const locale = (i18n.resolvedLanguage || i18n.language || "en").replace("_", "-")
        return new Intl.DateTimeFormat(locale, {
            month: "long",
            year: "numeric",
            timeZone: "UTC",
        })
    }, [i18n.language, i18n.resolvedLanguage])

    const formatBookingPeriodLabel = useCallback((period: Dayjs) => (
        bookingPeriodFormatter.format(new Date(Date.UTC(period.year(), period.month(), 1)))
    ), [bookingPeriodFormatter])

    const runningBookingPeriodKeys = useMemo(() => new Set(
        (monthlyClosings || [])
            .filter(closing => closing.isProcessing)
            .map(closing => closing.period.format("YYYY-MM"))
    ), [monthlyClosings])

    const bookingPeriodOptions = useMemo((): ISelectOption<string>[] => {
        if (!currentBookingPeriod) return []

        const latestDoneClosingPeriod = monthlyClosings
            ?.filter(closing => !closing.isProcessing)
            .reduce<Dayjs | null>((latest, closing) => {
                if (!latest || closing.period.isAfter(latest, "month")) {
                    return closing.period
                }
                return latest
            }, null)

        const firstOpenBookingPeriod = latestDoneClosingPeriod
            ? latestDoneClosingPeriod.clone().add(1, "month").startOf("month")
            : currentBookingPeriod.clone().startOf("month")
        const endPeriod = dayjs.max(currentBookingPeriod.clone().startOf("month"), dayjs().startOf("month"))

        if (firstOpenBookingPeriod.isAfter(endPeriod)) {
            return [{
                label: formatBookingPeriodLabel(endPeriod),
                value: endPeriod.format("YYYY-MM"),
            }]
        }

        const options: ISelectOption<string>[] = []
        let iter = firstOpenBookingPeriod.clone()

        while (iter.isSameOrBefore(endPeriod, "month")) {
            const periodKey = iter.format("YYYY-MM")
            if (runningBookingPeriodKeys.has(periodKey)) {
                iter = iter.add(1, "month")
                continue
            }

            options.push({
                label: formatBookingPeriodLabel(iter),
                value: periodKey,
            })
            iter = iter.add(1, "month")
        }

        return options
    }, [currentBookingPeriod, formatBookingPeriodLabel, monthlyClosings, runningBookingPeriodKeys])

    useEffect(() => {
        if (!currentBookingPeriod || bookingPeriodOptions.length === 0) return

        setSelectedBookingPeriodKey(prev => {
            if (prev && bookingPeriodOptions.some(option => option.value === prev)) {
                return prev
            }
            return bookingPeriodOptions[bookingPeriodOptions.length - 1]?.value || null
        })
    }, [bookingPeriodOptions, currentBookingPeriod])

    const isLoadingBookingPeriods = isLoadingMonthlyClosings || typeof currentBookingPeriod === "undefined"

    const handleReactivate = async () => {
        if (!availabilityDate || !selectedBookingPeriodKey || isAvailabilityDateInvalid) {
            return
        }

        const latestMonthlyClosings = await refetchMonthlyClosings()
        const latestRunningBookingPeriodKeys = new Set(
            (latestMonthlyClosings?.items || monthlyClosings || [])
                .filter(closing => closing.isProcessing)
                .map(closing => closing.period.format("YYYY-MM"))
        )
        if (latestRunningBookingPeriodKeys.has(selectedBookingPeriodKey)) {
            toastMessages.show({ title: t("assetReactivation.error.bookingPeriodLocked"), type: "error" })
            return
        }

        const selectedBookingPeriod = dayjs(selectedBookingPeriodKey, "YYYY-MM").startOf("month")

        confirmModals.show({
            title: t("assetReactivation.confirmTitle"),
            children: t("assetReactivation.confirmMessage"),
            confirmButtonLabel: t("common.confirm"),
            theme: "danger",
            maxWidth: "480px",
            onConfirm: async (close) => {
                close?.()
                setIsSubmitting(true)
                try {
                    const result = await AssetService.reactivateAsset(assetId, availabilityDate, selectedBookingPeriod)
                    if (result.success) {
                        toastMessages.show({ title: t("assetReactivation.successToast"), type: "default" })
                        onClose()
                        onSuccess?.()
                    } else {
                        // Handle error messages
                        let errorMessage = t("message.systemError")
                        if (result.error === 'notFound') {
                            errorMessage = t("assetReactivation.error.notFound")
                        } else if (result.error === 'noPermission') {
                            errorMessage = t("assetReactivation.error.noPermission")
                        } else if (result.error) {
                            // Check for known 409 conflict messages
                            if (result.error.includes("not disposed")) {
                                errorMessage = t("assetReactivation.error.notDisposed")
                            } else if (result.error.includes("not the current version")) {
                                errorMessage = t("assetReactivation.error.notCurrentVersion")
                            } else if (result.error.includes("already in progress")) {
                                errorMessage = t("assetReactivation.error.alreadyInProgress")
                            } else if (result.error.includes("Booking period")) {
                                errorMessage = t("assetReactivation.error.bookingPeriodLocked")
                            } else if (result.error.includes("Availability date")) {
                                errorMessage = t("assetReactivation.error.invalidAvailabilityDate")
                            } else {
                                errorMessage = result.error
                            }
                        }
                        toastMessages.show({ title: errorMessage, type: "error" })
                    }
                } catch (e) {
                    toastMessages.showSystemError()
                } finally {
                    setIsSubmitting(false)
                }
            }
        })
    }

    const footer = (
        <Flex justifyContent="flex-end" gap="1.2rem">
            <FlexItem>
                <Button
                    theme="secondary"
                    label={t("common.cancel")}
                    onClick={onClose}
                    disabled={isSubmitting}
                />
            </FlexItem>
            <FlexItem>
                <Button
                    theme="primary"
                    label={t("assetReactivation.reactivate")}
                    onClick={handleReactivate}
                    disabled={isSubmitting || isLoadingAvailabilityBounds || isLoadingBookingPeriods || !availabilityDate || !selectedBookingPeriodKey || isAvailabilityDateInvalid}
                    loading={isSubmitting}
                />
            </FlexItem>
        </Flex>
    )

    return (
        <Modal
            title={t("assetReactivation.title")}
            onClose={onClose}
            footer={footer}
            maxWidth="580px"
            isBusy={isSubmitting}
        >
            <Flex flow="column" gap="2.4rem">
                <FlexItem>
                    <Text type="p">
                        {relatedRequestNumber && relatedRequestId ? (
                            <Trans
                                i18nKey="assetReactivation.infoWithCr"
                                values={{ crNumber: relatedRequestNumber }}
                            >
                                You are about to reactivate an asset that was disposed as part of customer request
                                <Link
                                    to={Routes.reservationEdit.getPath({ reservationId: relatedRequestId })}
                                    className="link color-brand-blue"
                                >
                                    {relatedRequestNumber}
                                </Link>.
                            </Trans>
                        ) : (
                            t("assetReactivation.infoNoCr")
                        )}
                    </Text>
                </FlexItem>

                <FlexItem>
                    <div className="p-3 bg-grey-100 border-radius-1">
                        <Flex alignItems="center" gap="1.6rem">
                            <FlexItem>
                                <Lmid lmid={lmid} assetId={assetId} />
                            </FlexItem>
                            {articleDescription && (
                                <FlexItem>
                                    <Text type="p" className="color-grey-600">{articleDescription}</Text>
                                </FlexItem>
                            )}
                        </Flex>
                    </div>
                </FlexItem>

                <FlexItem>
                    <div style={{ width: "32rem", maxWidth: "100%" }}>
                        <FormField label={t("asset.expectedToBeAvailableOn")}>
                            <InputDateTime
                                type="datetime"
                                date={availabilityDate}
                                utc
                                minDate={minAvailabilityDate}
                                onDateChange={setAvailabilityDate}
                                isInvalid={isAvailabilityDateInvalid}
                                disabled={isLoadingAvailabilityBounds}
                            />
                        </FormField>
                        {isLoadingAvailabilityBounds && <Spinner className="py-2" size={20} />}
                        {isAvailabilityDateInvalid && (
                            <ValidationMessage>{t("validation.noEventsBeforeDate")}</ValidationMessage>
                        )}
                    </div>
                </FlexItem>

                <FlexItem>
                    <Text type="p" className="font-weight-bold mb-2">
                        {t("assetReactivation.explanation")}
                    </Text>
                    <ul className="pl-4">
                        <li className="mb-1">
                            <Text type="p">{t("assetReactivation.bullet1")}</Text>
                        </li>
                        <li className="mb-1">
                            <Text type="p">{t("assetReactivation.bullet2")}</Text>
                        </li>
                        <li>
                            <Text type="p">{t("assetReactivation.bullet3")}</Text>
                        </li>
                    </ul>
                </FlexItem>

                <FlexItem>
                    <div style={{ width: "32rem", maxWidth: "100%" }}>
                        <FormField label={t("assetReactivation.bookingPeriod")}>
                            <Select
                                value={selectedBookingPeriodKey}
                                options={bookingPeriodOptions}
                                onChange={option => setSelectedBookingPeriodKey(option?.value || null)}
                                isDisabled={isLoadingBookingPeriods || bookingPeriodOptions.length === 0}
                                onMenuOpen={() => { void refetchMonthlyClosings() }}
                                isInModal
                            />
                        </FormField>
                        {isLoadingBookingPeriods && <Spinner className="py-2" size={20} />}
                    </div>
                </FlexItem>
            </Flex>
        </Modal>
    )
}

export default ReactivateAssetModal
